var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { differenceInDays, format, formatDistanceToNow } from 'date-fns';
import { useUXPContext } from '@core/context/useUXPContext';
import { executeConfig } from '@core/context/useExecuteRequest';
import { Dropdown } from '@widget/popups/Dropdown';
import { useMessageBus } from '@realtime/messagebus/useMessageBus';
import Icon from '@icon/Icon';
import Button from '@widget/buttons/Button';
import IconButton from '@widget/buttons/IconButton';
import Checkbox from '@widget/forms/inputs/Checkbox';
import MultiSelect from '@widget/forms/inputs/MultiSelect';
import ProfileImage from '@widget/other/ProfileImage';
import SearchBox from '@widget/forms/inputs/SearchBox';
import DataList from '@widget/list/DataList';
import Chip from '@widget/other/Chip';
import LoadingSpinner from '@widget/loaders/LoadingSpinner';
import NotificationBlock from '@helper/NotificationBlock';
import useAlert from '@notifications/Alerts/useAlerts';
import useToast from '@notifications/Toast/useToast';
import useDebounce from '@hooks/useDebounce';
import { MessageBusChannel, PollingInterval, markReadUnread, resolvePlaceholders, useNotificationCenter, } from './notificationUtils';
import { NotificationServices } from './notificationServices';
import { hasValue } from '@utils';
const LocalStorageKey = 'uxpcore_nc_unreadonly';
const PAGE_SIZE = 20;
const SEVERITY_OPTIONS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const Timer = ({ datetime }) => {
    const [label, setLabel] = useState('');
    useEffect(() => {
        const update = () => {
            const date = new Date(datetime);
            if (isNaN(date.getTime())) {
                setLabel(datetime);
                return;
            }
            if (differenceInDays(new Date(), date) > 7) {
                setLabel(format(date, 'dd MMM yyyy'));
            }
            else {
                setLabel(formatDistanceToNow(date, { addSuffix: true }));
            }
        };
        update();
        const id = setInterval(update, 60000);
        return () => clearInterval(id);
    }, [datetime]);
    return _jsx("span", { className: "uxpcore_notificationcenter__item-time", children: label });
};
const ExpandableText = ({ text, maxLines = 2, showMore, showLess, }) => {
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const pRef = useRef(null);
    useEffect(() => {
        const el = pRef.current;
        if (!el)
            return;
        const check = () => {
            if (!expanded) {
                setIsOverflowing(el.scrollHeight > el.clientHeight);
            }
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [expanded]);
    return (_jsxs("div", { className: "uxpcore_expandabletext__container", children: [_jsx("p", { ref: pRef, className: classNames('uxpcore_notificationcenter__item-message', {
                    'uxpcore_notificationcenter__item-message--clamped': !expanded,
                }), style: !expanded ? { WebkitLineClamp: maxLines } : undefined, children: text }), isOverflowing && (_jsx("span", { className: "uxpcore_expandabletext__toggle", onClick: (e) => { e.stopPropagation(); setExpanded((v) => !v); }, children: expanded ? showLess : showMore }))] }));
};
const CategoryActionButton = ({ action, notification }) => {
    const context = useUXPContext();
    const toast = useToast();
    const alerts = useAlert();
    const params = Object.assign({ notificationId: notification.id, objectKey: notification.objectKey || '', objectType: notification.objectType || '', category: notification.category, severity: notification.severity }, (notification.additionalParams || {}));
    const handleClick = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        e.stopPropagation();
        const { actionType } = action;
        const p = {};
        for (const { key, value } of action.parameters || [])
            p[key] = resolvePlaceholders(value, params);
        try {
            if (actionType === 'link') {
                window.location.href = resolvePlaceholders(action.url || '', params);
            }
            else if (actionType === 'service') {
                yield context.executeService(action.app || '', action.service || '', p);
                if (action.successMessage)
                    toast.success(action.successMessage);
            }
            else if (actionType === 'modelAction') {
                yield context.executeAction(action.model || '', action.action || '', p);
                if (action.successMessage)
                    toast.success(action.successMessage);
            }
        }
        catch (_a) {
            if (action.errorMessage)
                alerts.error(action.errorMessage);
        }
    }), [action, params, context, toast, alerts]);
    return (_jsx(Button, { title: action.iconOnly ? undefined : action.label, leftIcon: action.icon, variant: action.variation || 'secondary', size: "smaller", onClick: handleClick, iconOnly: action.iconOnly }));
};
const NotificationItem = ({ notification, category, isToggling, showMore, showLess, onToggleRead, }) => {
    var _a;
    const sev = ((_a = notification.severity) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || 'low';
    const params = Object.assign({ notificationId: notification.id, objectKey: notification.objectKey || '', objectType: notification.objectType || '', category: notification.category, severity: notification.severity }, (notification.additionalParams || {}));
    const detailsUrl = (category === null || category === void 0 ? void 0 : category.detailsLinkTemplate)
        ? resolvePlaceholders(category.detailsLinkTemplate, params)
        : null;
    return (_jsxs("div", { className: classNames('uxpcore_notificationcenter__item', `uxpcore_notificationcenter__item--${sev}`, { 'uxpcore_notificationcenter__item--read': notification.hasRead }, { 'uxpcore_notificationcenter__item--clickable': !!detailsUrl }), onClick: () => { if (detailsUrl)
            window.location.href = detailsUrl; }, children: [_jsxs("div", { className: "uxpcore_notificationcenter__item-header", children: [_jsx("span", { className: classNames('uxpcore_notificationcenter__item-severity', `uxpcore_notificationcenter__item-severity--${sev}`), children: notification.severity }), hasValue(notification === null || notification === void 0 ? void 0 : notification.internalState) && (_jsx(Chip, { label: (notification === null || notification === void 0 ? void 0 : notification.internalState) || '', size: "small", variant: 'info' })), _jsx(Timer, { datetime: notification.datetime }), detailsUrl && (_jsx(IconButton, { icon: "phr eye", variant: "secondary", borderless: true, onClick: (e) => { e === null || e === void 0 ? void 0 : e.stopPropagation(); window.location.href = detailsUrl; } })), _jsx(IconButton, { icon: notification.hasRead ? 'phr envelope-open' : 'phr envelope', variant: "secondary", borderless: true, loading: isToggling, onClick: (e) => { e === null || e === void 0 ? void 0 : e.stopPropagation(); onToggleRead(notification.id, notification.hasRead); } })] }), _jsx("div", { className: "uxpcore_notificationcenter__item-body", children: _jsx(ExpandableText, { text: notification.message, showMore: showMore, showLess: showLess }) }), (category === null || category === void 0 ? void 0 : category.actions) && category.actions.length > 0 && (_jsx("div", { className: "uxpcore_notificationcenter__item-actions", children: category.actions.map((action, idx) => (_jsx(CategoryActionButton, { action: action, notification: notification }, idx))) }))] }));
};
/**
 * @export
 */
const NotificationCenter = () => {
    const context = useUXPContext();
    const [counter, setCounter] = useState(0);
    const [selectedTab, setSelectedTab] = useState(null);
    const [readUnreadItems, setReadUnreadItems] = useState(new Set());
    const [unreadOnly, setUnreadOnly] = useState(() => {
        try {
            return localStorage.getItem(LocalStorageKey) === 'true';
        }
        catch (_a) {
            return false;
        }
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [severity, setSeverity] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const debouncedSearch = useDebounce(searchQuery, 400);
    const { isLoading: categoriesLoading, categories, totalUnread } = useNotificationCenter(counter);
    // Tab order follows the server-defined priority order
    const [tabOrder, setTabOrder] = useState([]);
    useEffect(() => {
        if (categories.length > 0) {
            const ordered = categories.map(c => c.id);
            setTabOrder(ordered);
            setSelectedTab((prev) => { var _a; return (_a = prev !== null && prev !== void 0 ? prev : ordered[0]) !== null && _a !== void 0 ? _a : null; });
        }
    }, [categories]);
    const displayCategories = useMemo(() => tabOrder.map(id => categories.find(c => c.id === id)).filter(Boolean), [tabOrder, categories]);
    useMessageBus(context, MessageBusChannel, () => setCounter((c) => c + 1));
    useEffect(() => {
        const id = setInterval(() => setCounter((c) => c + 1), PollingInterval);
        return () => clearInterval(id);
    }, []);
    const notificationDataFn = useCallback((max, token, args) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const { data } = yield executeConfig(context, NotificationServices.getNotifications({
            category: selectedTab || '',
            max,
            lastPageToken: token,
            search: (args === null || args === void 0 ? void 0 : args.search) || '',
            unreadOnly: (args === null || args === void 0 ? void 0 : args.unreadOnly) || false,
            severity: ((_a = args === null || args === void 0 ? void 0 : args.severity) === null || _a === void 0 ? void 0 : _a.join(',')) || '',
        }));
        return {
            items: (data === null || data === void 0 ? void 0 : data.notifications) || [],
            pageToken: (data === null || data === void 0 ? void 0 : data.nextPageToken) || '',
            total: data === null || data === void 0 ? void 0 : data.total,
        };
    }), [selectedTab, context, counter]);
    const args = useMemo(() => ({ search: debouncedSearch, unreadOnly, severity, tab: selectedTab }), [debouncedSearch, unreadOnly, severity, selectedTab]);
    const handleToggleRead = useCallback((id, currentRead) => __awaiter(void 0, void 0, void 0, function* () {
        setReadUnreadItems((prev) => new Set(prev).add(id));
        yield markReadUnread(context, id, !currentRead);
        setReadUnreadItems((prev) => { const next = new Set(prev); next.delete(id); return next; });
        setCounter((c) => c + 1);
    }), [context]);
    const handleUnreadOnlyChange = useCallback((val) => {
        setUnreadOnly(val);
        try {
            localStorage.setItem(LocalStorageKey, String(val));
        }
        catch (_a) { }
    }, []);
    const currentCategory = categories.find((c) => c.id === selectedTab);
    const $L = context.$L.bind(context);
    const hasCategories = displayCategories.length > 0;
    const panelContent = (_jsxs("div", { className: "uxpcore_notificationcenter__panel", children: [_jsxs("div", { className: "uxpcore_notificationcenter__panel-header", children: [_jsx("span", { className: "uxpcore_notificationcenter__panel-title", children: $L('uxp-core.notification-center.title') }), _jsxs("div", { className: "uxpcore_notificationcenter__panel-header-actions", children: [_jsx(SearchBox, { value: searchQuery, onChange: setSearchQuery, placeholder: $L('uxp-core.notification-center.search-placeholder') }), _jsx(IconButton, { icon: "phr arrow-clockwise", variant: "secondary", loading: categoriesLoading, onClick: (e) => { e === null || e === void 0 ? void 0 : e.stopPropagation(); setCounter((c) => c + 1); } }), _jsx(IconButton, { icon: "phr x", variant: "secondary", onClick: (e) => { e === null || e === void 0 ? void 0 : e.stopPropagation(); setIsOpen(false); } })] })] }), categoriesLoading && !hasCategories ? (_jsx(LoadingSpinner, { spinnerOnly: true })) : !hasCategories ? (_jsx(NotificationBlock, { message: $L('uxp-core.notification-center.no-notifications') })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_notificationcenter__tabs", children: displayCategories.map((cat) => (_jsxs("button", { className: classNames('uxpcore_notificationcenter__tab', {
                                'uxpcore_notificationcenter__tab--active': selectedTab === cat.id,
                            }), onClick: (e) => { e.stopPropagation(); setSelectedTab(cat.id); }, children: [cat.icon && (_jsx("span", { className: "uxpcore_notificationcenter__tab-icon", children: _jsx(Icon, { icon: cat.icon }) })), _jsx("span", { className: "uxpcore_notificationcenter__tab-label", children: cat.label }), cat.alertCount > 0 && (_jsx("span", { className: "uxpcore_notificationcenter__tab-count", children: cat.alertCount > 99 ? '99+' : cat.alertCount }))] }, cat.id))) }), _jsx(DataList, { className: "uxpcore_notificationcenter__list", data: notificationDataFn, pageSize: PAGE_SIZE, args: args, showFooter: false, showEndOfContent: false, renderItem: (n) => (_jsx(NotificationItem, { notification: n, category: currentCategory, isToggling: readUnreadItems.has(n.id), showMore: $L('uxp-core.notification-center.show-more'), showLess: $L('uxp-core.notification-center.show-less'), onToggleRead: handleToggleRead })), renderNoItems: () => (_jsx(NotificationBlock, { message: $L('uxp-core.notification-center.no-notifications') })) }), _jsxs("div", { className: "uxpcore_notificationcenter__footer", children: [_jsx(Checkbox, { checked: unreadOnly, onChange: handleUnreadOnlyChange, label: $L('uxp-core.notification-center.show-unread-only') }), _jsx(MultiSelect, { className: "uxpcore_notificationcenter__severity-filter", dropdownClassname: "uxpcore_notificationcenter__severity-filter-dropdown", options: SEVERITY_OPTIONS.map(s => ({ label: s, value: s })), selected: severity, onChange: (vals) => setSeverity(vals), placeholder: $L('uxp-core.notification-center.severity-all'), position: "top-right", hideDoneButton: true, hideClearButton: true, renderPlaceholder: {
                                    renderCustomPill: () => (_jsx(Chip, { size: "small", variant: severity.length > 0 ? 'primary' : 'secondary', label: severity.length === 0
                                            ? $L('uxp-core.notification-center.severity-all')
                                            : `${severity.length}/${SEVERITY_OPTIONS.length}` }))
                                } })] })] }))] }));
    const trigger = (_jsxs("div", { className: "uxpcore_notificationcenter__trigger", children: [_jsx(ProfileImage, { image: "phr bell", bgColor: "transparent" }), totalUnread > 0 && (_jsx("span", { className: "uxpcore_notificationcenter__badge", children: totalUnread > 99 ? '99+' : totalUnread }))] }));
    return (_jsx(Dropdown, { trigger: trigger, content: panelContent, isOpen: isOpen, onToggle: () => setIsOpen((o) => !o), position: "bottom-right", className: "uxpcore_notificationcenter__dropdown", contentClassName: "uxpcore_notificationcenter__panel-wrapper" }));
};
export default NotificationCenter;
